import React from "react";
import { Info } from "lucide-react";

export const isVisible = (f, a) => {
  const c = f.show_if;
  if (!c) return true;
  const v = a[c.field];
  return Array.isArray(v) ? v.some((x) => c.in.includes(x)) : c.in.includes(v);
};

const base = "w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0072CE]/30 focus:border-[#0072CE]";

function Control({ f, value, onChange, invalid }) {
  const cls = `${base} ${invalid ? "border-rose-400" : "border-slate-200"}`;
  const tid = `field-${f.key}`;
  if (f.type === "textarea") return <textarea rows={3} className={cls} value={value || ""} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} data-testid={tid} id={tid} />;
  if (f.type === "select")
    return (
      <select className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} id={tid}>
        <option value="">Select…</option>
        {f.options.map((o) => <option key={o}>{o}</option>)}
      </select>
    );
  if (f.type === "multiselect") {
    const arr = value || [];
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={tid} id={tid} role="group">
        {f.options.map((o) => {
          const on = arr.includes(o);
          return (
            <button
              type="button"
              key={o}
              aria-pressed={on}
              onClick={() => onChange(on ? arr.filter((x) => x !== o) : [...arr, o])}
              data-testid={`${tid}-opt-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-[#0B2A4A] text-white border-[#0B2A4A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"} ${invalid && !arr.length ? "border-rose-300" : ""}`}
            >
              {o}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <input
      type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
      className={cls}
      value={value ?? ""}
      placeholder={f.placeholder}
      onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
      data-testid={tid}
      id={tid}
    />
  );
}

export default function FieldInput({ f, answers, setAnswer, flagged }) {
  if (!isVisible(f, answers)) return null;
  const wide = f.type === "textarea" || f.type === "multiselect";
  return (
    <div className={`${wide ? "md:col-span-2" : ""} ${flagged ? "rounded-lg ring-2 ring-rose-200 p-2 -m-2" : ""}`}>
      <label htmlFor={`field-${f.key}`} className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-1.5">
        {f.label}
        {f.required ? <span className="text-rose-500" aria-label="required">*</span> : <span className="text-slate-400 font-normal">optional</span>}
        {f.help && (
          <span className="group relative inline-flex" tabIndex={0} aria-label={f.help}>
            <Info size={12} className="text-slate-400" />
            <span className="pointer-events-none absolute left-4 top-0 z-20 w-64 rounded-md bg-slate-900 text-white text-[11px] font-normal leading-snug p-2 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity">{f.help}</span>
          </span>
        )}
      </label>
      <Control f={f} value={answers[f.key]} onChange={(v) => setAnswer(f.key, v)} invalid={flagged} />
    </div>
  );
}
